/**
 * Upgrade Modal — surfaces 402 `plan_required` API responses to the user.
 *
 * The admin SPA fetch wrapper (apps/admin/src/api.ts) throws an `ApiError`
 * with `status === 402` and `code === 'plan_required'` whenever a tenant
 * tries to use a feature gated above their plan tier (e.g. Starter calling a
 * Pro endpoint, or Pro calling a Scale endpoint). The handler in
 * apps/api/src/middleware/plan.ts responds with:
 *
 *   {
 *     error: 'this feature requires a higher plan',
 *     code: 'plan_required',
 *     required: ['pro','scale'],
 *     current: 'starter',
 *     field?: 'image_url'   // field-level gates only
 *   }
 *
 * Wiring:
 *   1. `installUpgradeModalInterceptor()` (called once from admin.ts on
 *      boot) patches the global `fetch` to look at every JSON response. When
 *      it sees `{ code: 'plan_required' }` it dispatches an
 *      `upgrade-required` CustomEvent on `window` so any listener — most
 *      importantly the modal itself — can react. The patch returns the
 *      original Response untouched so existing `ApiError` handlers in
 *      admin.ts still fire normally; the modal is purely additive.
 *
 *   2. `mountUpgradeModal()` injects the modal HTML into <body>, wires its
 *      close button, and subscribes to the `upgrade-required` event. It is
 *      idempotent — calling it twice is a no-op.
 *
 *   3. `showUpgradeModal(detail)` can also be called directly (e.g. from a
 *      try/catch around an ApiError) without going through the fetch
 *      interceptor. The Section F4 spec uses it via the dispatched event.
 *
 * Why a DOM CustomEvent instead of a store/observable? The admin SPA is
 * vanilla TS — no framework, no Redux, no signals. CustomEvent is the
 * lightest cross-module bus available and Playwright can dispatch one
 * directly from `page.evaluate(...)` to drive the modal headlessly.
 */

export interface UpgradeRequiredDetail {
  /** Required plan tier(s) — at least one of these unlocks the feature. */
  required: string[];
  /** The caller's current plan. */
  current: string;
  /**
   * Human-friendly feature label (optional). Falls back to a generic line if
   * omitted. The 402 response itself doesn't carry this — callers can pass
   * it through when they invoke `showUpgradeModal()` directly.
   */
  feature?: string | null;
  /** Field name for field-level gates (e.g. 'image_url'). Optional. */
  field?: string | null;
}

const MODAL_ID = 'upgrade-modal-overlay';
const EVENT_NAME = 'upgrade-required';

// ---------------------------------------------------------------------------
// Plan ordering + copy helpers.
// ---------------------------------------------------------------------------

const PLAN_ORDER: Record<string, number> = { starter: 0, pro: 1, scale: 2 };
const PLAN_LABEL: Record<string, string> = {
  starter: 'Starter',
  pro: 'Pro',
  scale: 'Scale',
};

/**
 * Pick the lowest plan in `required` so we describe the smallest upgrade
 * needed, not the largest. e.g. required=['pro','scale'] on a Starter →
 * "requires the Pro plan" (Pro covers it).
 */
function lowestRequiredPlan(required: string[]): string {
  const sorted = [...required].sort((a, b) => (PLAN_ORDER[a] ?? 99) - (PLAN_ORDER[b] ?? 99));
  return sorted[0] ?? 'pro';
}

function planLabel(plan: string): string {
  return PLAN_LABEL[plan] ?? plan;
}

// ---------------------------------------------------------------------------
// Billing URL resolver — per-tenant if we can read a slug from the URL,
// otherwise the platform-level /billing.
// ---------------------------------------------------------------------------

function resolveBillingUrl(): string {
  // Per-tenant routing lives at /t/{slug}/… on app.lanebooker.ca and at
  // / on {slug}.lanebooker.ca. Try the path first so it works on both.
  try {
    const m = window.location.pathname.match(/^\/t\/([^/]+)\b/);
    if (m && m[1]) return `/t/${encodeURIComponent(m[1])}/billing`;
    // Subdomain form — host is {slug}.lanebooker.ca (or .pages.dev preview).
    const host = window.location.hostname;
    if (host.endsWith('.lanebooker.ca') && host !== 'app.lanebooker.ca' && host !== 'www.lanebooker.ca') {
      return '/billing';
    }
  } catch {
    /* no-op — fall through */
  }
  return '/billing';
}

// ---------------------------------------------------------------------------
// Modal markup + mount
// ---------------------------------------------------------------------------

function modalTemplate(): string {
  // Standalone HTML — no template literals interpolated at mount time so we
  // can safely .innerHTML this. All dynamic copy is set via textContent in
  // `applyDetail()` below.
  return `
<div class="modal-overlay" id="${MODAL_ID}" data-testid="upgrade-modal" role="dialog" aria-modal="true" aria-labelledby="upgrade-modal-title">
  <div class="modal modal-sm" id="upgrade-modal">
    <div class="modal-header">
      <h2 id="upgrade-modal-title" data-testid="upgrade-modal-title">Upgrade required</h2>
      <button class="modal-close" id="upgrade-modal-close" data-testid="upgrade-modal-close" aria-label="Close">&times;</button>
    </div>
    <div class="modal-body">
      <p id="upgrade-modal-body" data-testid="upgrade-modal-body">
        This feature requires a higher plan.
      </p>
      <p class="field-hint" id="upgrade-modal-current" data-testid="upgrade-modal-current">
        Your current plan: <strong></strong>
      </p>
      <p class="field-hint hidden" id="upgrade-modal-field" data-testid="upgrade-modal-field">
        Restricted field: <code></code>
      </p>
    </div>
    <div class="modal-footer">
      <button class="btn btn-outline" id="upgrade-modal-cancel" data-testid="upgrade-modal-cancel">Not now</button>
      <a class="btn btn-primary" id="upgrade-modal-cta" data-testid="upgrade-modal-cta" href="/billing">
        View billing &amp; upgrade
      </a>
    </div>
  </div>
</div>`;
}

function ensureMounted(): HTMLElement {
  let el = document.getElementById(MODAL_ID);
  if (el) return el;
  const wrap = document.createElement('div');
  wrap.innerHTML = modalTemplate().trim();
  el = wrap.firstElementChild as HTMLElement;
  document.body.appendChild(el);
  wireCloseHandlers(el);
  return el;
}

function wireCloseHandlers(root: HTMLElement) {
  const close = () => root.classList.remove('open');
  root.querySelector<HTMLButtonElement>('#upgrade-modal-close')?.addEventListener('click', close);
  root.querySelector<HTMLButtonElement>('#upgrade-modal-cancel')?.addEventListener('click', close);
  // Click on the backdrop (the overlay itself, not its children) closes too.
  root.addEventListener('click', (e) => {
    if (e.target === root) close();
  });
  // Esc to close.
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && root.classList.contains('open')) close();
  });
}

function applyDetail(root: HTMLElement, detail: UpgradeRequiredDetail) {
  const required = Array.isArray(detail.required) && detail.required.length > 0
    ? detail.required
    : ['pro'];
  const lowest = lowestRequiredPlan(required);
  const lowestLbl = planLabel(lowest);

  const titleEl = root.querySelector<HTMLElement>('#upgrade-modal-title');
  if (titleEl) {
    titleEl.textContent = detail.feature
      ? `${detail.feature} requires the ${lowestLbl} plan`
      : `This feature requires the ${lowestLbl} plan`;
  }

  const bodyEl = root.querySelector<HTMLElement>('#upgrade-modal-body');
  if (bodyEl) {
    const requiredLabels = required.map(planLabel).join(' or ');
    bodyEl.textContent =
      `You're on the ${planLabel(detail.current)} plan. ` +
      `Upgrade to ${requiredLabels} to unlock this feature.`;
  }

  const currentEl = root.querySelector<HTMLElement>('#upgrade-modal-current strong');
  if (currentEl) currentEl.textContent = planLabel(detail.current);

  const fieldWrap = root.querySelector<HTMLElement>('#upgrade-modal-field');
  const fieldCode = root.querySelector<HTMLElement>('#upgrade-modal-field code');
  if (fieldWrap && fieldCode) {
    if (detail.field) {
      fieldCode.textContent = detail.field;
      fieldWrap.classList.remove('hidden');
    } else {
      fieldCode.textContent = '';
      fieldWrap.classList.add('hidden');
    }
  }

  const cta = root.querySelector<HTMLAnchorElement>('#upgrade-modal-cta');
  if (cta) cta.href = resolveBillingUrl();
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** Mount the modal into the DOM if not already there. Safe to call repeatedly. */
export function mountUpgradeModal(): void {
  if (typeof document === 'undefined') return;
  ensureMounted();
  // Subscribe once — use a sentinel to avoid double subscribers if the
  // function is called twice during HMR or by accident.
  if ((window as unknown as { __lbUpgradeMounted?: boolean }).__lbUpgradeMounted) return;
  (window as unknown as { __lbUpgradeMounted?: boolean }).__lbUpgradeMounted = true;
  window.addEventListener(EVENT_NAME, (e: Event) => {
    const detail = (e as CustomEvent<UpgradeRequiredDetail>).detail;
    if (!detail) return;
    showUpgradeModal(detail);
  });
}

/** Open the modal directly with a known detail payload. */
export function showUpgradeModal(detail: UpgradeRequiredDetail): void {
  const root = ensureMounted();
  applyDetail(root, detail);
  root.classList.add('open');
}

/** Dispatch the event without showing the modal directly — useful when the
 *  caller wants the same global plumbing as a 402 response would trigger. */
export function dispatchUpgradeRequired(detail: UpgradeRequiredDetail): void {
  window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail }));
}

// ---------------------------------------------------------------------------
// Fetch interceptor — sniffs 402 plan_required without consuming the body
// for the original caller.
// ---------------------------------------------------------------------------

/**
 * Patch `window.fetch` so any 402 JSON response with `code: 'plan_required'`
 * fires an `upgrade-required` event. The original Response is returned
 * untouched (we read a clone) so downstream `await res.json()` calls still
 * succeed.
 *
 * Idempotent — second call is a no-op.
 */
export function installUpgradeModalInterceptor(): void {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  const w = window as unknown as { __lbUpgradeFetchPatched?: boolean };
  if (w.__lbUpgradeFetchPatched) return;
  w.__lbUpgradeFetchPatched = true;

  const originalFetch = window.fetch.bind(window);
  window.fetch = async function patchedFetch(...args: Parameters<typeof fetch>): Promise<Response> {
    const res = await originalFetch(...args);
    if (res.status === 402) {
      // Clone before reading so the original body stream is untouched for
      // the caller's `await res.json()`.
      const cloned = res.clone();
      try {
        const ct = cloned.headers.get('content-type') ?? '';
        if (ct.includes('application/json')) {
          const data = (await cloned.json()) as {
            code?: string;
            required?: string[];
            current?: string;
            field?: string | null;
          };
          if (data && data.code === 'plan_required') {
            dispatchUpgradeRequired({
              required: Array.isArray(data.required) ? data.required : ['pro'],
              current: data.current ?? 'starter',
              field: data.field ?? null,
            });
          }
        }
      } catch {
        // Ignore parse errors — the original response still flows through.
      }
    }
    return res;
  };
}
